import { FC, Fragment, StrictMode, useContext, useEffect } from 'react';
import Head from 'next/head';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import Gleap from 'gleap';
import mixpanel from 'mixpanel-browser';
import { normalizeUrlAndExtractParams } from 'helpers/mixpanel';
import { SWRConfig } from 'swr';
import 'react-datepicker/dist/react-datepicker.css';
import * as Sentry from '@sentry/react';
import { CbFallbackUI } from 'ui/components/CbErrorBoundary';
import { maybeReloadOnNavigate, mergeAuth, monkeyPatchXMLHttpRequest, shouldReloadOnAuthChange } from 'utils';
import '@workplacearcade/styles/typography.css';
import { MPThemeContextProvider, SpectatorViewProvider } from '../providers';
import { AppContext, MPAppContextProvider } from '../providers/MPAppContextProvider';
import { setupSentry } from '../helpers/setupSentry';
import { useRouter } from 'next/router';
import { usePathname, useSearchParams } from 'next/navigation';
import { Routes } from '../types';
import { FeaturesProvider, RoleProvider } from 'providers';
import { GleapIdentifier } from 'components/GleapIdentifier';
import Script from 'next/script';
import { TermsModal } from '../containers/NavigationContainer';
import { MigrationModal } from '../components/MigrationModal';
import { WaitlistModal } from '../components/WaitlistModal';
declare global {
  interface Window {
    mergeAuth: typeof mergeAuth;
    prerenderReady: boolean;
    Tooltip: any;
    Appcues: any;
    AppcuesSettings: {
      enableURLDetection: boolean;
    };
    /* 
      This refers to the Javascript channel that will be attached if this app is loaded in
      a WebView by the Upduo application.
    */
    Upduo?: any;
  }
}
const MPAuthProvider = dynamic(async () => await import('../providers/MPAuthContextProvider'), {
  ssr: false
});
const MPEmbeddedProvider = dynamic(async () => await import('../providers/MPEmbeddedContextProvider'), {
  ssr: false
});
const MPEmbeddedListener = dynamic(async () => await import('../providers/MPEmbeddedListener'), {
  ssr: false
});
const MPEmbeddedLinkRewriter = dynamic(async () => await import('../providers/MPEmbeddedLinkRewriter'), {
  ssr: false
});
const MPAuthInterceptor = dynamic(async () => await import('../providers/MPAuthInterceptor'), {
  ssr: false
});
const MainLayout = dynamic(async () => await import('../components/MainLayout/MainLayout'), {
  ssr: false
});
const App: FC<AppProps> = ({
  Component,
  pageProps
}) => {
  const {
    title
  } = useContext(AppContext);
  const production = process.env.NODE_ENV === 'production';
  useEffect(() => {
    Gleap.initialize('2fIWl1ctLszhV0uux40dJL7oFJkJG9JB');
    if (production) {
      setupSentry();
    }
  }, []);
  const pathname = usePathname();
  useEffect(() => {
    Gleap.showFeedbackButton(pathname?.includes('login') || false);
  }, [pathname]);
  const MIXPANEL_TOKEN = process.env.MIXPANEL_API || 'FAKE_TOKEN_VALUE'; // Empty string won't work as fake token value

  useEffect(() => {
    const config = production ? {
      debug: false
    } : {
      debug: true
    };
    mixpanel.init(MIXPANEL_TOKEN, config);
  }, []);
  useEffect(() => {
    monkeyPatchXMLHttpRequest();
  }, []);

  // Helper method to rapidly 'spoof' being logged in as a user, if necessary
  useEffect(() => {
    window.mergeAuth = mergeAuth;
  }, []);
  useEffect(() => {
    if (window?.AppcuesSettings) {
      window.AppcuesSettings = {
        enableURLDetection: true
      };
    }
  }, []);

  /* When the logged-in user changes via the login flow or an external link, reload any
     instances that did not initiate the change in LocalStorage (native behavior of the
     Web API). Navigate back to the root as the current page may not be valid for the new
     credentials. Only redirect when the user identity actually changed — token rotations
     and profile-field refreshes write the same key but should not navigate away.
  */
  const reloadOnAuthChange = (e: StorageEvent) => {
    if (!shouldReloadOnAuthChange(e)) return;
    window.location.href = '/';
  };
  useEffect(() => {
    window.addEventListener('storage', reloadOnAuthChange);
    return () => window.removeEventListener('storage', reloadOnAuthChange);
  }, []);
  const searchParams = useSearchParams();
  const suppressLayout = (() => {
    // Suppress for /guides/:id, but not /manage/guides/:id
    if (!pathname?.match(/\/manage\/guides/) && pathname?.match(/\/guides/) && searchParams.get('id') || pathname?.match(/\/results/)) return true;
    return false;
  })();
  const SuppressableLayout = suppressLayout ? Fragment : MainLayout;
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="_app.tsx">
        <meta name="robots" content="noindex" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" data-sentry-element="meta" data-sentry-source-file="_app.tsx" />
        <title>{title || 'Arcade'}</title>
        <link rel="icon" href="/images/favicon.ico" />
      </Head>
      <Script src="https://fast.appcues.com/206255.js"
    // loads the script just before any next.js page is rendered or hydration occurs
    strategy="beforeInteractive" data-sentry-element="Script" data-sentry-source-file="_app.tsx" />
      <Sentry.ErrorBoundary
    /*
    Providing our custom CbErrorBoundary component as the UI for Sentry's error boundary.
    This allows the customer to easily reload the page.
    */ fallback={({
      error
    }) => <CbFallbackUI error={error} />} data-sentry-element="unknown" data-sentry-source-file="_app.tsx">
        <MPEmbeddedProvider data-sentry-element="MPEmbeddedProvider" data-sentry-source-file="_app.tsx">
          <MPAuthProvider data-sentry-element="MPAuthProvider" data-sentry-source-file="_app.tsx">
            <MPEmbeddedListener data-sentry-element="MPEmbeddedListener" data-sentry-source-file="_app.tsx" />
            <MPAuthInterceptor data-sentry-element="MPAuthInterceptor" data-sentry-source-file="_app.tsx">
              <SWRConfig value={{
              revalidateOnFocus: false,
              revalidateOnReconnect: false,
              fetcher: url => fetch(url).then(res => res.json())
            }} data-sentry-element="SWRConfig" data-sentry-source-file="_app.tsx">
                <SpectatorViewProvider data-sentry-element="SpectatorViewProvider" data-sentry-source-file="_app.tsx">
                  <FeaturesProvider data-sentry-element="FeaturesProvider" data-sentry-source-file="_app.tsx">
                    <MPEmbeddedLinkRewriter data-sentry-element="MPEmbeddedLinkRewriter" data-sentry-source-file="_app.tsx" />
                    <RoleProvider data-sentry-element="RoleProvider" data-sentry-source-file="_app.tsx">
                      <GleapIdentifier data-sentry-element="GleapIdentifier" data-sentry-source-file="_app.tsx" />
                      <MigrationModal data-sentry-element="MigrationModal" data-sentry-source-file="_app.tsx" />
                      <TermsModal data-sentry-element="TermsModal" data-sentry-source-file="_app.tsx" />
                      <WaitlistModal data-sentry-element="WaitlistModal" data-sentry-source-file="_app.tsx" />
                      <SuppressableLayout data-sentry-element="SuppressableLayout" data-sentry-source-file="_app.tsx">
                        <Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
                      </SuppressableLayout>
                    </RoleProvider>
                  </FeaturesProvider>
                </SpectatorViewProvider>
              </SWRConfig>
            </MPAuthInterceptor>
          </MPAuthProvider>
        </MPEmbeddedProvider>
      </Sentry.ErrorBoundary>
    </>;
};
const AppWithContext: FC<AppProps> = props => {
  const router = useRouter();
  useEffect(() => {
    router.events.on('routeChangeComplete', maybeReloadOnNavigate);
    return () => router.events.off('routeChangeComplete', maybeReloadOnNavigate);
  }, []);
  useEffect(() => {
    const handleRouteChange = url => {
      const {
        normalizedUrl,
        params
      } = normalizeUrlAndExtractParams(url);
      mixpanel.track('Page Viewed', {
        page: normalizedUrl,
        ...params // Include extracted parameters as properties
      });
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  // For rendered cards, ignore all wrappers and context, just show the card
  if (router.pathname.startsWith(Routes.RenderedCards)) {
    return <MPThemeContextProvider>
        <Sentry.ErrorBoundary>
          <props.Component {...props.pageProps} />
        </Sentry.ErrorBoundary>
      </MPThemeContextProvider>;
  }
  return <StrictMode data-sentry-element="StrictMode" data-sentry-component="AppWithContext" data-sentry-source-file="_app.tsx">
      <MPThemeContextProvider data-sentry-element="MPThemeContextProvider" data-sentry-source-file="_app.tsx">
        <MPAppContextProvider data-sentry-element="MPAppContextProvider" data-sentry-source-file="_app.tsx">
          <App {...props} data-sentry-element="App" data-sentry-source-file="_app.tsx" />
        </MPAppContextProvider>
      </MPThemeContextProvider>
    </StrictMode>;
};
export default AppWithContext;